import * as React from 'react';

import { cn } from '../../lib/utils';

// ================================================================= //
//          Reusable Input Component for Forms                       //
// ================================================================= //
// A styled, accessible, and reusable input component that aligns    //
// with the application's design system. It supports standard input  //
// props, error states, and is built for accessibility.              //
// ================================================================= //

// --- TypeScript Props Interface ---
// Extends the standard HTML input attributes to ensure all native
// props are supported (e.g., type, placeholder, name, etc.).
export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  // You can add custom props here if needed in the future,
  // for example, an `error` prop to toggle error styling.
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          // --- Base Styles ---
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
          // --- File Input Specific Styles ---
          'file:border-0 file:bg-transparent file:text-sm file:font-medium',
          // --- Placeholder Styles ---
          'placeholder:text-muted-foreground',
          // --- Focus & Accessibility Styles ---
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          // --- Disabled State ---
          'disabled:cursor-not-allowed disabled:opacity-50',
          // --- Error State (for potential future use with react-hook-form) ---
          // Example: You could pass a className like 'border-destructive'
          // when a form field has an error.
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

// Set a display name for easier debugging in React DevTools.
Input.displayName = 'Input';

export { Input };
